import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { LuCircleCheck, LuCircleAlert, LuUser, LuMapPin, LuPhone, LuMessageCircle } from "react-icons/lu";
import PhoneInput from '../../components/common/PhoneInput';

const baseURL = process.env.REACT_APP_BASEURL;

const JoinPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const isSubmitting = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: { countryCode: '+1', number: '' },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Canada'
    },
    whatsappOptIn: false,
    whatsappNumber: { countryCode: '+1', number: '' }
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${baseURL}/v1/invitations/public/${token}`);
        setInvitation(response.data);
        setFormData(prev => ({
          ...prev,
          name: response.data.customerName || '',
        }));
      } catch (err) {
        setError(err.response?.data?.error || 'VERIFICATION_FAILED');
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Full name is required';
    if (!formData.address.city.trim()) errors.city = 'City is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting.current) return;
    if (!validate()) return;

    isSubmitting.current = true;
    setLoading(true);

    try {
      await axios.post(`${baseURL}/v1/invitations/complete/${token}`, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'SUBMISSION_FAILED');
      isSubmitting.current = false;
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
        <Spinner style={{ color: '#28B0C4' }} />
        <p className="text-muted small mt-2">Verifying your invitation...</p>
      </div>
    );
  }

  if (error === 'INVITE_EXPIRED') {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center p-5 bg-white rounded-4 shadow-sm" style={{ maxWidth: '500px' }}>
          <div style={{ fontSize: '3rem' }}>⏰</div>
          <h5 className="fw-bold mt-3">Invitation Expired</h5>
          <p className="text-muted">This invitation link has expired.</p>
          <p className="text-muted small">Please contact {invitation?.businessName} for a new invitation.</p>
        </div>
      </Container>
    );
  }

  if (error === 'ALREADY_ACTIVATED') {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center p-5 bg-white rounded-4 shadow-sm" style={{ maxWidth: '500px' }}>
          <div style={{ fontSize: '3rem' }}>✓</div>
          <h5 className="fw-bold mt-3" style={{ color: '#10b981' }}>Profile Already Completed</h5>
          <p className="text-muted">Welcome back, {invitation?.customerName}!</p>
          <p className="text-muted small">Your profile is already set up with {invitation?.businessName}.</p>
        </div>
      </Container>
    );
  }

  if (error === 'INVITE_NOT_FOUND') {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center p-5 bg-white rounded-4 shadow-sm" style={{ maxWidth: '500px' }}>
          <LuCircleAlert size={48} className="text-danger mb-3" />
          <h5 className="fw-bold">Invitation Not Found</h5>
          <p className="text-muted">We couldn't find an invitation associated with this link.</p>
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center p-5 bg-white rounded-4 shadow-sm" style={{ maxWidth: '500px' }}>
          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
            style={{ width: 80, height: 80, background: '#d1fae5' }}>
            <LuCircleCheck size={40} style={{ color: '#10b981' }} />
          </div>
          <h4 className="fw-bold text-dark">Welcome, {formData.name}!</h4>
          <p className="text-muted">Your profile has been successfully created.</p>
          <p className="text-muted small">{invitation?.businessName} has been notified.</p>
          <p className="text-muted small mt-3">You can now close this window.</p>
        </div>
      </Container>
    );
  }

  return (
    <div className="min-vh-100 bg-light py-5">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <div className="text-center mb-4">
              <h4 className="fw-bold">{invitation?.businessName}</h4>
            </div>
            
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="p-4 bg-white border-bottom text-center">
                <h5 className="fw-bold mb-1">👋 You've been invited!</h5>
                <p className="text-muted small mb-0">
                  Complete your profile to get started with {invitation?.businessName}'s customer portal.
                </p>
              </div>
              
              <Card.Body className="p-4 p-md-5">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-semibold text-muted">Email (read-only)</Form.Label>
                    <Form.Control
                      type="email"
                      value={invitation?.customerEmail}
                      disabled
                      style={{ background: '#f1f5f9', border: '1.5px solid #e2e8f0', borderRadius: '10px' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-semibold">Full Name*</Form.Label>
                    <Form.Control
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.name}
                      placeholder="Your full name"
                      style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                  </Form.Group>

                  <PhoneInput
                    label="Phone"
                    value={formData.phone}
                    onChange={(val) => setFormData(prev => ({ ...prev, phone: val }))}
                  />

                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-semibold">Street Address</Form.Label>
                    <Form.Control
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      placeholder="Street address"
                      style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                    />
                  </Form.Group>

                  <Row className="mb-4">
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold">City*</Form.Label>
                        <Form.Control
                          name="address.city"
                          value={formData.address.city}
                          onChange={handleInputChange}
                          isInvalid={!!formErrors.city}
                          placeholder="City"
                          style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.city}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6} className="mt-4 mt-sm-0">
                      <Form.Group>
                        <Form.Label className="small fw-semibold">Province/State</Form.Label>
                        <Form.Control
                          name="address.state"
                          value={formData.address.state}
                          onChange={handleInputChange}
                          placeholder="Province/State"
                          style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold">Postal/Zip Code</Form.Label>
                        <Form.Control
                          name="address.zipCode"
                          value={formData.address.zipCode}
                          onChange={handleInputChange}
                          placeholder="Postal code"
                          style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} className="mt-4 mt-sm-0">
                      <Form.Group>
                        <Form.Label className="small fw-semibold">Country</Form.Label>
                        <Form.Select
                          name="address.country"
                          value={formData.address.country}
                          onChange={handleInputChange}
                          style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}
                        >
                          <option value="Canada">Canada</option>
                          <option value="USA">USA</option>
                          <option value="UK">UK</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Card className="border-0 bg-light rounded-4 mb-5">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <LuMessageCircle size={18} className="text-success" />
                        <span className="fw-bold small">Stay updated via WhatsApp</span>
                      </div>
                      <Form.Check 
                        type="checkbox"
                        id="whatsappOptIn"
                        name="whatsappOptIn"
                        label="Enable WhatsApp notifications"
                        checked={formData.whatsappOptIn}
                        onChange={handleInputChange}
                        className="small fw-semibold mb-3"
                      />
                      {formData.whatsappOptIn && (
                        <PhoneInput
                          label="WhatsApp Number (if different from phone)"
                          value={formData.whatsappNumber}
                          onChange={(val) => setFormData(prev => ({ ...prev, whatsappNumber: val }))}
                        />
                      )}
                    </Card.Body>
                  </Card>

                  <Button 
                    type="submit" 
                    className="w-100 py-3 rounded-4 fw-bold shadow-sm"
                    style={{ background: '#28B0C4', border: 'none', color: 'white' }}
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner size="sm" className="me-2" /> Saving...</>
                    ) : 'Complete My Profile'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default JoinPortal;
